import type { EvmChainId } from '~/service/EVM/config/types';
import { xsollaZkChain } from '~/service/EVM/chains';
import { formatUnits } from 'viem';

/**
 * Опции форматирования
 */
type FormattingOptions = {
    precision?: number;
    skipRoundingForIntegers?: boolean; // пропускать округление для целых чисел?
    returnStringForNull?: boolean; // возвращать '' для null/undefined?
    maxLength?: number; // максимальная длина строки (для обрезки)
};

/**
 * Сокращает адрес до формата "prefix...suffix"
 * @param address адрес кошелька
 * @param options Опции сокращения:
 *   - prefixLength: длина префикса (по умолчанию 6)
 *   - suffixLength: длина суффикса (по умолчанию 4)
 *   - separator: разделитель (по умолчанию '...')
 * @returns Сокращенная строка или пустая строка для null/undefined
 * @example
 * toShortAddress('0x742d35Cc6634C0532925a3b844Bc454e4438f44e') → '0x742d...f44e'
 */
export const toShortAddress = (
    address?: string | null,
    options?: {
        prefixLength?: number;
        suffixLength?: number;
        separator?: string;
    }
): string => {
    if (!address) return '';

    const {
        prefixLength = 6,
        suffixLength = 4,
        separator = '...'
    } = options || {};

    const prefix = address.slice(0, prefixLength);
    const suffix = suffixLength > 0 ? address.slice(-suffixLength) : '';

    if (suffixLength <= 0) {
        return address.length > prefixLength
            ? `${prefix}${separator}`
            : address;
    }

    return address.length > prefixLength + suffixLength
        ? `${prefix}${separator}${suffix}`
        : address;
};

/**
 * Проверяет, является ли строка ссылкой на видео
 * @param val
 */
export const isVideo = (val: string) => (/\.(mp4|webm|ogg)$/i).test(val);

/**
 * Извлекает расширение файла из строки
 * @param val
 */
export const getFileExtension = (val: string): string => {
    return val.split('?')[0]?.split('.').pop() || '';
};

/**
 * Проверяет, является ли строка валидным числом
 * @param value
 */
export const isValidNumber = (value: string) => {
    const regex = /^(?!\.)(\d+(\.\d*)?)$/;
    return regex.test(value);
};

/**
 * Возвращает иконку валюты с учетом сети
 * @param iconName
 * @param chainId
 */
export const showCurrencyIcon = (iconName: string, chainId?: EvmChainId) => {
    if (chainId === xsollaZkChain.id) return 'XSOLLAZK';
    return iconName;
};

/**
 * Универсальная функция форматирования чисел.
 *
 * @param value Число или null/undefined.
 * @param optionsOrPrecision Можно передать:
 *   - число (precision) → `formatNumber(123.456, 2)`
 *   - объект (options) → `formatNumber(123.456, { precision: 2, skipRoundingForIntegers: true })`
 *
 * @returns число, строку или undefined (в зависимости от опций).
 */
export function formatNumber(
    value: number | null | undefined,
    optionsOrPrecision?: number | FormattingOptions
): number | string | undefined {
    const options: FormattingOptions = typeof optionsOrPrecision === 'number'
        ? { precision: optionsOrPrecision }
        : optionsOrPrecision || {};

    const {
        precision = 2,
        skipRoundingForIntegers = false,
        returnStringForNull = false
    } = options;

    if (value === null || value === undefined) {
        return returnStringForNull ? '' : undefined;
    }

    if (skipRoundingForIntegers && !value.toString().includes('.')) {
        return value;
    }

    const result = parseFloat(value.toFixed(precision));

    return returnStringForNull ? result.toString() : result;
}

/**
 * Форматирует строку, обрезая её при превышении максимальной длины
 * @param value
 * @param options
 * @example
 * formatString('long text string', { maxLength: 6 }) → 'long t...'
 */
export function formatString(value: string, options?: FormattingOptions) {
    const { maxLength = 8 } = options || {};
    return value.length > maxLength
        ? value.slice(0, maxLength) + '...'
        : value;
}

/**
 * Универсальное форматирование для чисел и строк
 * @param value
 * @param options
 */
export function formatValue<T extends number | string>(value: T, options?: FormattingOptions) {
    if (typeof value === 'number') {
        return formatNumber(value, options);
    }
    if (typeof value === 'string') {
        return formatString(value, options);
    }
    return value;
}

/**
 * Форматирует сумму валюты из минимальных единиц (wei/gwei) в читаемый формат
 * @param params Параметры форматирования
 * @param params.value Сумма в минимальных единицах (wei/gwei)
 * @param params.currencyType Тип валюты (eth, matic и др.)
 * @param params.unitType Единицы измерения (wei/gwei)
 * @returns Отформатированная строка суммы
 * @example
 * formatCurrency({ value: 1000000000000000000n, currencyType: 'eth' }) → '1'
 */
export function formatCurrency({ value, currencyType, unitType = 'wei' }: {
    value: bigint;
    currencyType: User['network'];
    unitType?: 'wei' | 'gwei';
}) {
    let decimals;

    switch (currencyType.toLowerCase()) {
        case 'matic':
            decimals = 4;
            break;
        case 'eth':
            decimals = 18;
            break;
        default:
            decimals = 18;
            break;
    }

    if (unitType === 'gwei') {
        decimals = 9;
    }
    else if (unitType === 'wei') {
        decimals = 18;
    }

    const readableValue = formatUnits(value, decimals);
    return parseFloat(readableValue).toFixed(4).replace(/\.?0+$/, '');
}

/**
 * Форматирует цены в строку, согласно правилам округления и форматирования (из статьи)
 * если 0, возвращает '0'
 * если отрицательная добавляется '-' в начало
 * если больше 1000000 добавляется суффикс 'M' в конце строки (и тд B, T)
 * разряды чисел разделяются запятой
 * если имеет целое значение, то знаков после запятой - 2
 * если имеет только дробную часть без ведущих нулей, то знаков после запятой - 4
 * если имеет только дробную часть с ведущими нулями, то максимальное количество отображаемых ведущих нулей - 4, затем округленные значащие цифры (не более 4х)
 * если ведущих нулей 5 и более - сокращаются подстрочным индексом с указанием количества нулей
 * @param {number} price - цена, которую будем форматировать
 * @returns {string} отформатированная строка
 */
export function formatPrice(price: number): string {
    // обработка нуля
    if (price === 0) return '0';

    const absPrice = Math.abs(price); // модуль числа
    const sign = price < 0 ? '-' : '';

    if (absPrice >= 1e12) {
        return sign + (absPrice / 1e12).toFixed(2) + 'T';
    }
    if (absPrice >= 1e9) {
        return sign + (absPrice / 1e9).toFixed(2) + 'B';
    }
    if (absPrice >= 1e6) {
        return sign + (absPrice / 1e6).toFixed(2) + 'M';
    }

    // для чисел >= 1
    if (absPrice >= 1) {
        const parts = absPrice.toFixed(2).split('.');
        const integerPart = parts[0] !== undefined ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
        return sign + integerPart + '.' + parts[1];
    }

    // для чисел < 1
    const str = absPrice.toString();
    // eslint-disable-next-line prefer-const
    let [_, decimalPart] = str.split('.');
    if (!decimalPart) decimalPart = '';

    let zeroCount = 0; // количество ведущих нулей
    while (decimalPart[zeroCount] === '0' && zeroCount < decimalPart.length) {
        zeroCount++;
    }

    // значащие цифры после запятой (максимально 4)
    let significantDigits = decimalPart.slice(zeroCount, zeroCount + 4);

    // округление
    if (decimalPart.length > zeroCount + 4) {
        const roundingDigitStr = decimalPart[zeroCount + 4];
        if (roundingDigitStr !== undefined) {
            const roundingDigit = parseInt(roundingDigitStr, 10);
            if (!isNaN(roundingDigit) && roundingDigit >= 5) {
                const digitsValue = parseInt(significantDigits, 10) + 1;
                significantDigits = digitsValue.toString().padStart(4, '0').slice(0, 4);
            }
        }
    }

    // удаление лишних нулей в конце
    significantDigits = significantDigits.replace(/0+$/, '');

    // форматирование
    if (zeroCount === 0) {
        // нет ведущих нулей: 0.1234
        return significantDigits.length > 0
            ? `${sign}0.${significantDigits}`
            : `${sign}0`;
    }
    else if (zeroCount <= 4) {
        // 1-4 нуля: 0.0001234
        return `${sign}0.${'0'.repeat(zeroCount)}${significantDigits}`;
    }
    else {
        // 5+ нулей: 0.0₅678
        const subscript = zeroCount.toString()
            .split('')
            .map(d => '₀₁₂₃₄₅₆₇₈₉'[parseInt(d)])
            .join('');
        return `${sign}0.0${subscript}${significantDigits}`;
    }
}
